import * as amplitude from "@amplitude/analytics-react-native"
import FeedbackButton from "@common/components/feedback/FeedbackButton"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { setAgentNotifCount } from "@data/redux/actions/liveMode"
import { cleanMediaUrl } from "@data/constants/apiConstants"
import { liveService } from "@data/services/liveService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import moment from "moment/moment"
import React, { useContext, useEffect, useState } from "react"
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { ChampionImageM } from "../../student/champions/ChampionImageM"
import {IconImageM} from "@common/components/icons/IconsM";
import MediaPreviewModal from "@common/messenger/MediaPreviewModal"
import GenerateActivityModal from "../../tutor/students/GenerateActivityModal"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { formatFullDateTimeNumeric } from "../../data/utility/Utils"
import { checkIsPdf } from "@common/components/pdf/pdfHelpers"

const AgentSummaryModal = (props) => {
    const { student, onExit = () => {} } = props
    const dimensions = useContext(DimensionsContext)

    const [showInfo, setShowInfo] = useState(true)
    const [showMediaPreview, setShowMediaPreview] = useState(false)
    const [mediaPreviewFile, setMediaPreviewFile] = useState(null)
    const [conversationId, setConversationId] = useState(null)
    const [showGenerateActivityModal, setShowGenerateActivityModal] = useState(false)
    const [initialImageForModal, setInitialImageForModal] = useState(null)

    const agentSummaries = (student?.conversations || []).filter(
        (conv) => !(conv.status === "ongoing" && (
            conv.trigger_context === "starting_block" ||
            conv.trigger_context === "starting_block_study_program"
        ))
    )

    const sortedSummaries = [...agentSummaries].sort(
        (a, b) =>
            moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
    )
    const [initialNotifCount, setInitialNotifCount] = useState(0)
    const [feedbacks, setFeedbacks] = useState({})

    useEffect(() => {
        if (student?.id) {
            amplitude.track("checkpoint.tutor.summary_modal.open")
        }
    }, [student?.id])

    useEffect(() => {
        if (agentSummaries?.length) {
            const existingFeedbacks = {}
            agentSummaries.forEach((summary) => {
                if (summary.tutor_feedback !== null) {
                    existingFeedbacks[summary.id] = summary.tutor_feedback
                }
            })
            setFeedbacks(existingFeedbacks)
        }
    }, [student?.id])

    useEffect(() => {
        if (student?.id) {
            // props.apiRequest(liveServiceV2.getLiveSessionV2, { id: props.liveSessionId }, {})
            const currentCount =
                agentNotifCount.length > 0 ? agentNotifCount[0].count : 0
            setInitialNotifCount(currentCount)
            props.setAgentNotifCount(props.liveSessionId, student?.id, 0)
        }
    }, [student?.id])

    useApiStatus(liveService.giveAgentFeedback, null, false, (data) => {
        props.apiRequest(
            liveServiceV2.getLiveSessionV2,
            { id: props.liveSessionId },
            {}
        )
        setFeedbacks((prev) => ({
            ...prev,
            [data.conversation_id]: data.feedback_value,
        }))
    })

    const agentNotifCount = props.agentNotifCounts.filter((agentNotif) => {
        return (
            agentNotif.studentId === student?.id &&
            agentNotif?.liveSessionId === props.liveSessionId
        )
    })

    const renderTimeStamp = (dateString) => {
        const formattedDate = formatFullDateTimeNumeric(dateString)

        return (
            <View style={styles.timeStampContainer}>
                <FontAwesome5
                    name="clock"
                    size={12}
                    color={theme.colors.grey}
                    style={styles.timeIcon}
                />
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    style={styles.timeText}
                >
                    {formattedDate}
                </TextM>
            </View>
        )
    }

    const renderStatusIcon = (isNew) => {
        if (!isNew) return null

        return (
            <View style={styles.nouveauBadge}>
                <TextM
                    fontSize={10}
                    color={theme.colors.warning}
                    fontWeight="Bold"
                >
                    NOUVEAU
                </TextM>
            </View>
        )
    }

    const markNewSummaries = () => {
        if (initialNotifCount === 0 || !student?.id) return sortedSummaries
        return sortedSummaries.map((summary, index) => ({
            ...summary,
            isNew: index < initialNotifCount,
        }))
    }

    const summariesWithNewStatus = markNewSummaries()

    const handleFeedback = (conversationId, value) => {
        if (feedbacks[conversationId] !== undefined) return
        amplitude.track("checkpoint.tutor.feedback.submit")

        props.apiRequest(
            liveService.giveAgentFeedback,
            { id: conversationId },
            {
                feedback_type: "tutor",
                feedback_value: value,
            }
        )
        setFeedbacks((prev) => ({ ...prev, [conversationId]: value }))
    }

    const handleExit = () => {
        amplitude.track("checkpoint.tutor.summary_modal.close")

        onExit()
    }

    const renderFeedbackButtons = (conversationId, tutorFeedback) => {
        // If there's a tutor feedback, show it as selected and disabled
        if (tutorFeedback !== null) {
            return (
                <View style={styles.feedbackContainerNew}>
                    <FeedbackButton
                        onPress={() => {}}
                        disabled={true}
                        isSelected={true}
                        isPositive={tutorFeedback === 1}
                        tooltipText={
                            tutorFeedback === 1 ? "Utile" : "Pas utile"
                        }
                        minimal={true}
                    />
                </View>
            )
        }

        // If feedback is already given in current session, only show the selected button
        if (feedbacks[conversationId] !== undefined) {
            return (
                <View style={styles.feedbackContainerNew}>
                    <FeedbackButton
                        onPress={() => {}}
                        disabled={true}
                        isSelected={true}
                        isPositive={feedbacks[conversationId] === 1}
                        tooltipText={
                            feedbacks[conversationId] === 1
                                ? "Utile"
                                : "Pas utile"
                        }
                        minimal={true}
                    />
                </View>
            )
        }

        // If no feedback yet, show both options
        return (
            <View style={styles.feedbackContainerNew}>
                <FeedbackButton
                    onPress={() => handleFeedback(conversationId, 1)}
                    disabled={feedbacks[conversationId] !== undefined}
                    isSelected={feedbacks[conversationId] === 1}
                    isPositive={true}
                    tooltipText="Utile"
                    minimal={true}
                />
                <FeedbackButton
                    onPress={() => handleFeedback(conversationId, -1)}
                    disabled={feedbacks[conversationId] !== undefined}
                    isSelected={feedbacks[conversationId] === -1}
                    isPositive={false}
                    tooltipText="Pas utile"
                    minimal={true}
                />
            </View>
        )
    }

    const renderHelpStatusBadge = (status) => {
        if (status === "ongoing") {
            return (
                <View style={[styles.statusBadge, styles.ongoingBadge]}>
                    <FontAwesome5
                        name="hourglass-half"
                        size={12}
                        color={theme.colors.grey}
                        style={styles.statusIcon}
                    />
                    <TextM
                        fontSize={12}
                        color={theme.colors.grey}
                        fontWeight="Bold"
                    >
                        Non terminée
                    </TextM>
                </View>
            )
        }

        const isTutorNeeded = status === "tutor_needed"
        const isNewActivityGeneration = status === "new_activity_generation"
        const isInterrupted = status === "interrupted"

        let config
        if (isTutorNeeded) {
            config = {
                icon: "exclamation-circle",
                color: theme.colors.error,
                label: "Besoin d'aide",
                style: styles.tutorNeededBadge,
            }
        } else if (isNewActivityGeneration) {
            config = {
                icon: "plus-circle",
                color: theme.colors.success,
                label: "Nouvelle activité générée",
                style: styles.newActivityBadge,
            }
        } else if (isInterrupted) {
            config = {
                icon: "ban",
                color: theme.colors.warning,
                label: "Interrompu",
                style: styles.interruptedBadge,
            }
        } else {
            config = {
                icon: "check-circle",
                color: theme.colors.primary,
                label: "Terminé",
                style: styles.completedBadge,
            }
        }

        return (
            <View style={[styles.statusBadge, config.style]}>
                <FontAwesome5
                    name={config.icon}
                    size={12}
                    color={config.color}
                    style={styles.statusIcon}
                />
                <TextM fontSize={12} color={config.color} fontWeight="Bold">
                    {config.label}
                </TextM>
            </View>
        )
    }

     const handlePhotoClick = (imageUrl, conversationId) => {
         const cleanedUrl = cleanMediaUrl(imageUrl)
         const fileName = `checkpoint_${conversationId}_${Date.now()}`

         setMediaPreviewFile({
             uri: cleanedUrl,
             name: fileName,
         })
         setConversationId(conversationId)
         setShowMediaPreview(true)
     }

     const handleCreateActivityFromPreview = () => {
         setShowMediaPreview(false)

         // Prepare the image for GenerateActivityModal
         // Pass conversationId so backend can use existing image from conversation
         const imageFile = {
             uri: mediaPreviewFile?.uri,
             name: mediaPreviewFile?.name || `file_${Date.now()}`,
             mimeType: checkIsPdf(mediaPreviewFile?.uri) ? "application/pdf" : "image/png",
             conversationId: conversationId, // Backend will use this to fetch the image
         }

         setInitialImageForModal(imageFile)
         setShowGenerateActivityModal(true)
     }

     const handleCloseGenerateActivityModal = () => {
         setShowGenerateActivityModal(false)
         setInitialImageForModal(null)
         setMediaPreviewFile(null)
         setConversationId(null)
     }

    const renderAttachments = (attachments, conversationId) => {
        if (!attachments || attachments.length === 0) return null;

        return (
            <View style={styles.attachmentsContainer}>
                <TextM
                    fontSize={14}
                    fontWeight="Bold"
                    color={theme.colors.grey}
                    style={styles.attachmentsTitle}
                >
                    Photos prises:
                </TextM>
                <ScrollView horizontal style={styles.attachmentsScroll}>
                    {attachments.map((url, index) => (
                        <TouchableOpacity
                            key={index}
                            onPress={() => handlePhotoClick(url, conversationId)}
                            style={styles.photoThumbnail}
                        >
                            <IconImageM
                                uri={cleanMediaUrl(url)}
                                width={50}
                                height={50}
                                style={styles.thumbnailImage}
                            />
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            </View>
        )
    }

    const renderSummaryComponent = (item, index) => {
        const {
            id,
            summary_title,
            summary,
            created_at,
            status,
            isNew,
            tutor_feedback,
            agent_message,
            attachments,
        } = item


        const showFeedbackButtons =
            status === "ended" ||
            status === "tutor_needed" ||
            status === "new_activity_generation"
        const isLastMessage =
            !agent_message?.proposed_responses ||
            agent_message?.proposed_responses.length === 0

        return (
            <TouchableOpacity
                key={index}
                style={[
                    styles.summaryItem,
                    {
                        borderLeftColor:
                            status === "tutor_needed"
                                ? theme.colors.error
                                : status === "new_activity_generation"
                                ? theme.colors.success
                                : theme.colors.primary,
                    },
                ]}
            >
                <View style={styles.summaryHeader}>
                    <View style={styles.titleContainer}>
                        <TextM
                            fontSize={16}
                            fontWeight="Bold"
                            color={
                                status === "ongoing"
                                    ? theme.colors.primary
                                    : status === "new_activity_generation"
                                    ? theme.colors.success
                                    : theme.colors.primary
                            }
                            style={styles.summaryTitle}
                            numberOfLines={1}
                        >
                            {status === "ongoing"
                                ? "Discussion non terminée"
                                : summary_title || "Discussion terminée"}
                        </TextM>
                        {renderStatusIcon(isNew)}
                    </View>
                    {renderHelpStatusBadge(status)}
                </View>

                <View style={styles.summaryTextContainer}>
                    <ScrollView
                        style={styles.summaryScrollView}
                        nestedScrollEnabled={true}
                    >
                        <TextM
                            fontSize={14}
                            fontWeight="Regular"
                            style={styles.summaryText}
                            wrap
                        >
                            {summary}
                        </TextM>
                    </ScrollView>
                    {/*{key_info && Object.keys(key_info).length > 0 && (*/}
                    {/*    <View style={styles.keyInfoContainer}>*/}
                    {/*        {Object.entries(key_info).map(([k, v]) => (*/}
                    {/*            <View key={k} style={styles.keyInfoRow}>*/}
                    {/*                <TextM fontSize={11} color={theme.colors.grey} style={styles.keyInfoLabel}>*/}
                    {/*                    {k.replace(/_/g, ' ')}:*/}
                    {/*                </TextM>*/}
                    {/*                <TextM fontSize={11} color={theme.colors.greyDark} style={styles.keyInfoValue}>*/}
                    {/*                    {String(v)}*/}
                    {/*                </TextM>*/}
                    {/*            </View>*/}
                    {/*        ))}*/}
                    {/*    </View>*/}
                    {/*)}*/}
                    {renderAttachments(
                        attachments ||
                        item.checkpoint_attachments ||
                        item.student_response_attachments ||
                        item.attachments ||
                        item.media ||
                        [],
                        item.id
                    )}
                    <View style={styles.summaryFooter}>
                        {renderTimeStamp(created_at)}
                        {showFeedbackButtons && isLastMessage && (
                            <View style={styles.feedbackWrapper}>
                                {renderFeedbackButtons(id, tutor_feedback)}
                            </View>
                        )}
                    </View>
                </View>
            </TouchableOpacity>
        )
    }

    return (
        <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
                <ChampionImageM
                    name={"sam"}
                    variant={"main"}
                    style={{
                        position: "absolute",
                        left: -20,
                        opacity: 0.08,
                    }}
                    eye
                    width={200}
                />
                <View>
                    <TextM
                        fontSize={18}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={styles.modalTitle}
                    >
                        Points d&apos;avancement de{" "}
                        {student?.display_name ?? ""}
                    </TextM>
                    <TextM
                        style={{
                            marginBottom: 10,
                            textAlign: "center",
                            marginHorizontal: 10,
                        }}
                        fontSize={11}
                        wrap
                        color={theme.colors.grey}
                    >
                        Je suis Sam, ton assistant, je suis là pour te relayer
                        si besoin. Retrouve ici les résumés de mes échanges avec{" "}
                        {student?.firstname}.
                    </TextM>
                </View>
                <TouchableOpacity
                    style={styles.closeButton}
                    onPress={handleExit}
                    hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
                >
                    <FontAwesome5
                        name="times"
                        size={24}
                        color={theme.colors.primary}
                    />
                </TouchableOpacity>
            </View>

            <ScrollView
                style={styles.summariesContainer}
                contentContainerStyle={styles.summariesContentContainer}
            >
                {summariesWithNewStatus.length > 0 ? (
                    summariesWithNewStatus.map((item, index) =>
                        renderSummaryComponent(item, index)
                    )
                ) : (
                    <View style={styles.emptyContainer}>
                        <View style={styles.emptyNotification}>
                            <FontAwesome5
                                name="comment-slash"
                                size={16}
                                color={theme.colors.primary}
                            />
                        </View>
                        <TextM fontSize={14} color={theme.colors.grey}>
                            Aucun point d&apos;avancement disponible pour le
                            moment.
                        </TextM>
                    </View>
                )}
            </ScrollView>

             {/* MediaPreviewModal for checkpoint photos */}
             <MediaPreviewModal
                 visible={showMediaPreview}
                 onDismiss={() => {
                     setShowMediaPreview(false)
                     setMediaPreviewFile(null)
                     setConversationId(null)
                 }}
                 file={mediaPreviewFile}
                 isExistingMessage={true}
                 onCreateActivity={handleCreateActivityFromPreview}
                 dimensions={dimensions}
             />

             {/* GenerateActivityModal with initial image */}
             {showGenerateActivityModal && props.navigation && (
                 <GenerateActivityModal
                     isOpen={showGenerateActivityModal}
                     onClose={handleCloseGenerateActivityModal}
                     student={student}
                     schoolSubjects={props.schoolSubjects}
                     nav={props.navigation}
                     initialImage={initialImageForModal}
                 />
             )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: "stretch",
        justifyContent: "center",
        position: "relative",
        flex: 1,
    },
    notificationButtonContainer: {
        alignItems: "center",
        justifyContent: "center",
        width: 50,
        height: 45,
        borderRadius: 10,
        backgroundColor: theme.colors.lightMore,
    },
    notificationButton: {
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    notificationBadge: {
        position: "absolute",
        top: -12,
        right: -16,
        backgroundColor: theme.colors.error,
        borderRadius: 10,
        width: 20,
        height: 20,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 2,
        borderColor: theme.colors.error,
    },

    modalContent: {
        paddingVertical: 30,
        paddingHorizontal: 16,
        width: "100%",
        height: "100%",
        backgroundColor: "#f8f9fa",
        borderRadius: 12,
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
        overflow: "hidden",
    },
    modalHeader: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: 20,
        width: "100%",
        position: "relative",
    },
    modalTitle: {
        textAlign: "center",
        paddingHorizontal: 10,
    },
    closeButton: {
        padding: 8,
        position: "absolute",
        right: 0,
    },
    summariesContentContainer: {
        paddingVertical: 8,
    },
    summaryItem: {
        padding: 16,
        borderRadius: 10,
        marginBottom: 12,
        borderLeftWidth: 4,
        borderLeftColor: theme.colors.primary,
        backgroundColor: "white",
        elevation: 3,
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowOpacity: 0.2,
        shadowRadius: 1.41,
        borderWidth: 0.5,
        borderColor: "#e0e0e0",
    },
    summaryHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
    },
    titleContainer: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        marginRight: 8,
    },
    summaryTitle: {
        flex: 1,
        marginRight: 8,
    },
    summaryTextContainer: {
        flex: 1,
    },
    summaryScrollView: {
        maxHeight: 120,
        marginBottom: 8,
    },
    summaryText: {
        lineHeight: 20,
    },
    summaryFooter: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
    },
    photoThumbnail: {
        marginRight: 8,
        borderRadius: 8,
        overflow: 'hidden',
    },
    thumbnailImage: {
        borderRadius: 8,
    },
    timeStampContainer: {
        flexDirection: "row",
        alignItems: "center",
    },
    timeIcon: {
        marginRight: 6,
    },
    timeText: {
        opacity: 0.8,
    },
    statusBadge: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    },
    ongoingBadge: {
        backgroundColor: theme.colors.lightMore,
    },
    tutorNeededBadge: {
        backgroundColor: theme.colors.errorLight,
    },
    completedBadge: {
        backgroundColor: `${theme.colors.primary}15`,
    },
    statusIcon: {
        marginRight: 6,
    },
    feedbackContainerNew: {
        flexDirection: "row",
        gap: 8,
    },
    feedbackWrapper: {
        flex: 1,
        alignItems: "flex-end",
    },
    nouveauBadge: {
        backgroundColor: theme.colors.accentLighter,
        borderColor: theme.colors.accent,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 2,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        elevation: 2,
    },
    emptyContainer: {
        padding: 24,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
    },
    emptyNotification: {
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 10,
        backgroundColor: theme.colors.lightMore,
    },
    attachmentsContainer: {
        marginTop: 12,
        marginBottom: 8,
        paddingLeft: 0,
    },
    attachmentsTitle: {
        marginBottom: 8,
    },
    attachmentsScroll: {
        width: "100%",
        alignSelf: "flex-start",
    },
    keyInfoContainer: {
        backgroundColor: theme.colors.lightMore,
        borderRadius: 6,
        padding: 8,
        marginTop: 6,
        marginBottom: 6,
        opacity: 0.85,
    },
    keyInfoRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 2,
    },
    keyInfoLabel: {
        marginRight: 4,
        fontWeight: "bold",
        opacity: 0.7,
    },
    keyInfoValue: {
        opacity: 0.85,
    },
    newActivityBadge: {
        backgroundColor: `${theme.colors.success}15`,
    },
    interruptedBadge: {
        backgroundColor: `${theme.colors.warning}15`,
    },
})

const mapStateToProps = (state) => {
    return {
        agentNotifCounts: state.liveMode?.agentNotifCounts,
        liveSessionId: state.liveMode?.liveSessionId,
        user: state.data.currentUser?.object,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setAgentNotifCount,
}

export default connect(mapStateToProps, mapDispatchToProps)(AgentSummaryModal)
